// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ReactNode } from "react";
import { createContext, useContext } from "react";

export interface FeatureFlags {
  scheduleChange: boolean;
  approvalFlow: boolean;
  aiAssistant: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  scheduleChange: false,
  approvalFlow: false,
  aiAssistant: false,
};

// Backend flag key (kebab-case) -> local flag name mapping
export const FLAG_KEYS: Record<keyof FeatureFlags, string> = {
  scheduleChange: "release-backoffice-schedule-change",
  approvalFlow: "release-backoffice-approval-flow",
  aiAssistant: "release-backoffice-ai-assistant",
};

const FeatureFlagContext = createContext<FeatureFlags>(DEFAULT_FLAGS);

export function FeatureFlagProvider({ flags, children }: { flags?: Partial<FeatureFlags>; children: ReactNode }) {
  const value = { ...DEFAULT_FLAGS, ...flags };
  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagContext);
}

export function mapFeatureFlags(raw: Record<string, unknown>): Partial<FeatureFlags> {
  return Object.fromEntries(
    (Object.entries(FLAG_KEYS) as [keyof FeatureFlags, string][]).map(([local, remote]) => [local, !!raw[remote]]),
  ) as Partial<FeatureFlags>;
}
